/**
 * Iter 34 — Reports MVP.
 *
 * Top-level page (NOT inside admin). Solo-friendly today; admin-friendly
 * when role-scoping arrives.
 *
 * Single page, four sections:
 *   1. Caseload Overview
 *   2. Compliance / Due-date Oversight
 *   3. Service Delivery
 *   4. Schedule / Coverage
 *
 * One filter bar at top scopes every card. Filters serialize to the
 * URL so reports are bookmarkable / shareable.
 *
 * Each card carries a stable `data-report-section` ID so a future
 * PDF/CSV export can target sections deterministically.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChartBar, Funnel, Users, GraduationCap, ClipboardText, Heartbeat,
  CalendarBlank, Warning, Sparkle, ArrowRight, Buildings, TrendUp, UserCircle,
  Camera, ArrowsClockwise, CaretDown, CaretUp, X, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProviderFidelityReport } from "@/components/fidelity/ProviderFidelityReport";
import PageShell from "@/components/layout/PageShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { authScopeKey, STALE } from "@/lib/queryClient";
import { RefreshChip } from "@/lib/formatUpdatedAgo";
// FP3-053 — Compact "Viewing: …" pill rendered next to the Reports
// title when a super admin has the sidebar context selector pinned.
// Mirrors the AdminShell pill on other admin pages.
import { useSuperAdminContext } from "@/lib/useSuperAdminContext";

function ReportsContextPill() {
  const { isSuperAdmin, orgId, orgName, schoolName } = useSuperAdminContext();
  if (!isSuperAdmin || !orgId) return null;
  const label =
    orgName && schoolName
      ? `${orgName} · ${schoolName}`
      : orgName || "Loading…";
  return (
    <span
      data-testid="reports-context-pill"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E6F1F8] border border-[#1F4F73]/30 text-[11.5px] text-[#0F3554]"
      title="Active org/school context (set via sidebar)"
    >
      <Buildings size={12} weight="duotone" className="text-[#1F4F73]" />
      <span className="font-medium truncate max-w-[36ch]">Viewing: {label}</span>
    </span>
  );
}

const STATUS_LABELS = {
  active: "IEPs",
  rti: "RTI",
  monitoring: "Monitoring",
  graduated: "Graduated",
};

const KIND_LABELS = {
  service_obligation_no_blocks: "No scheduled blocks",
  service_obligation_under_minutes: "Under-scheduled",
  service_obligation_over_minutes: "Over-scheduled",
  grade_constraint: "Grade-rule overlap",
  provider_unavailable_day: "Provider day-off",
  provider_blocked_time: "Provider blocked time",
  provider_partial_window: "Outside provider window",
  flex_day_outside_window: "Outside flex window",
  flex_day_closed: "Flex day closed",
  attendance_window: "Outside attendance window",
  cross_provider: "Cross-provider overlap",
};

const CANCEL_REASON_LABELS = {
  provider_unavailable: "Provider unavailable",
  student_absent: "Student absent",
  schedule_change: "Schedule change",
  other: "Other / unspecified",
};

const DOMAIN_TONE = {
  speech_language:  "bg-[#1E5A3F]",
  ot:               "bg-[#9C6B2E]",
  pt:               "bg-[#3F4756]",
  social_behavior:  "bg-[#7C3F58]",
  academics:        "bg-[#4A5C7A]",
  hearing_vision:   "bg-[#5B6770]",
  transition:       "bg-[#9E7A3D]",
  health_nursing:   "bg-[#B85C5C]",
  support_access:   "bg-[#6B7280]",
  enrichment_other: "bg-[#8B5A8C]",
};

// FP3-086 — The 10 broad Areas of Impact labels (same keys + labels as
// the Student Profile / Students page). Falls back to the raw key when
// not registered.
const IMPACT_LABELS = {
  speech_language:  "Speech-Language",
  ot:               "Occupational Therapy",
  pt:               "Physical Therapy",
  social_behavior:  "Social-Emotional / Behavior",
  academics:        "Academics",
  hearing_vision:   "Hearing / Vision",
  transition:       "Transition / Independent Living",
  health_nursing:   "Health / Nursing",
  support_access:   "Support / Access Services",
  enrichment_other: "Enrichment / Other",
};

const ELIGIBILITY_LABELS = {
  // Full Arizona keys (as seeded in students.eligibility_category)
  speech_language_impairment:    "SLI · Speech-Language Impairment",
  other_health_impairment:       "OHI · Other Health Impairment",
  autism:                        "ASD · Autism",
  specific_learning_disability:  "SLD · Specific Learning Disability",
  emotional_disturbance:         "ED · Emotional Disturbance",
  intellectual_disability:       "ID · Intellectual Disability",
  developmental_delay:           "DD · Developmental Delay",
  hearing_impairment:            "HI · Hearing Impairment",
  visual_impairment:             "VI · Visual Impairment",
  orthopedic_impairment:         "OI · Orthopedic Impairment",
  traumatic_brain_injury:        "TBI · Traumatic Brain Injury",
  multiple_disabilities:         "MD · Multiple Disabilities",
  deaf_blindness:                "DB · Deaf-Blindness",
  preschool_severe_delay:        "PSD · Preschool Severe Delay",
  // Abbreviated keys (in case downstream uses them)
  ed:               "Emotional Disturbance",
  sld:              "Specific Learning Disability",
  ohi:              "Other Health Impairment",
  asd:              "Autism Spectrum",
  sli:              "Speech-Language Impairment",
};

function fmtPct(n) {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ── Reusable building blocks ───────────────────────────────────────────────
function MetricTile({ label, value, sub, accent = "default", testid }) {
  const accentMap = {
    default: "border-[#E5E3DB] bg-white",
    green:   "border-[#1E5A3F]/20 bg-[#E1EAE4]/30",
    amber:   "border-[#9C6B2E]/30 bg-[#FDF8F3]",
    red:     "border-[#B85C5C]/25 bg-[#FBEFEC]/60",
    slate:   "border-[#3F4756]/15 bg-[#3F4756]/5",
  };
  return (
    <div data-testid={testid} className={`rounded-lg border ${accentMap[accent]} p-3`}>
      <p className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className="text-2xl font-bold text-[#1F2937] mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-[#6B7280] mt-1">{sub}</p>}
    </div>
  );
}

function HBar({ label, count, max, tone = "bg-[#3F4756]" }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="w-32 text-[#1F2937] truncate" title={label}>{label}</span>
      <div className="flex-1 bg-[#F3F1EC] h-3 rounded-sm overflow-hidden">
        <div className={`${tone} h-3 transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-[#1F2937] tabular-nums font-medium">{count}</span>
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children, id, action }) {
  return (
    <section id={id} className="space-y-3 scroll-mt-20" data-report-section={id}>
      <div className="flex items-end justify-between border-b border-[#E5E3DB] pb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="duotone" className="text-[#3F4756]" />
          <h2 className="text-base font-semibold text-[#1F2937] heading-font">{title}</h2>
          {subtitle && <span className="text-[11px] text-[#6B7280]">· {subtitle}</span>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReportCard({ title, subtitle, action, children, id, className = "" }) {
  return (
    <Card className={`border-[#E5E3DB] ${className}`} data-report-card={id}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font">{title}</CardTitle>
            {subtitle && <p className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent className="pt-1">{children}</CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
// Iter 37 — Reports split into two surfaces. The page accepts a
// `lockedLens` prop:
//   "admin"    → Admin Reports (school-wide oversight)
//   "provider" → Provider Reports ("My Reports", scoped to caller)
//   undefined  → Free toggle (legacy entry — kept for back-compat)
export default function ReportsPage({ lockedLens } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentProvider, setCurrentProvider] = useState(null);
  const { user, preferences } = useAuth();
  // FP3-086 — snapshot controls are super-admin only (the endpoints
  // are platform-global and now 403 for everyone else).
  const isSuperAdminUser = (user?.permission_role || "") === "super_admin";
  // FP3-119 — Fidelity-Off gating. When the viewer preference
  // `fidelity_monitoring="off"`, hide the ProviderFidelityReport
  // card that lives inside the Service Delivery tab. The rest of
  // Service Delivery (attendance, delivered-vs-scheduled) is NOT
  // fidelity-only and stays visible.
  const isFidelityOff = preferences?.fidelity_monitoring === "off";

  // FP3-029B5 — Admin family hitting `/reports` (provider lens) WITHOUT
  // `?as_provider=` must not silently fall back to the seeded
  // `is_current=True` provider (Sarah). Show a calm "Pick a provider"
  // empty state instead. Provider users keep landing on their own
  // reports as before.
  const isAdminFamily =
    !!user &&
    ["admin", "org_admin", "super_admin"].includes(user.permission_role);
  // FP3-111A — Assistant callers (permission_role="provider" +
  // provider_role_type="assistant" + supervising_provider_id set) get
  // a scoped Assigned Minutes view only. Overview / Compliance /
  // Service Delivery / Trends remain 403 for them (existing FP3-060
  // contract), so we skip those data fetches and auto-select the
  // Assigned Minutes tab.
  const isAssistantUser =
    !!user
    && (user.permission_role || "").toLowerCase() === "provider"
    && (user.provider_role_type || "").toLowerCase() === "assistant"
    && !!(user.supervising_provider_id || "").trim();
  const asProviderParam = searchParams.get("as_provider") || "";
  const adminNoProviderSelected =
    isAdminFamily && lockedLens === "provider" && !asProviderParam;

  // Iter 34 Phase 2 — Lens. Admin = school-wide; Provider = scoped to
  // the current provider. The lens drives both the default filter set
  // and which cards render. Iter 37 — locked when the route hard-pins
  // it; the toggle UI stays hidden.
  const [lens, setLens] = useState(() => {
    if (lockedLens) return lockedLens;
    return searchParams.get("lens") === "provider" ? "provider" : "admin";
  });

  // Load current provider so Provider lens can auto-scope. In solo
  // mode the "current provider" is the only one anyway, so this works
  // identically. FP3-029B4 — `api.getCurrentProvider` is now shadow-
  // aware (substitutes `/admin/providers/{id}` when the URL carries
  // `?as_provider=`). FP3-029B5 — skip the call entirely when an
  // admin lands on /reports without selecting a provider so we don't
  // accidentally hydrate Sarah's identity.
  useEffect(() => {
    if (adminNoProviderSelected) return;
    api.getCurrentProvider().then((r) => setCurrentProvider(r.data)).catch(() => {});
  }, [adminNoProviderSelected]);

  // Filter state — derived from URL so links are shareable. When locked
  // to provider lens, force provider_id to the current provider once
  // we know it.
  // FP3-086 — taxonomy filters: multi-select Eligibility + Areas of
  // Impact (comma-serialized in the URL). The legacy `domain`
  // (service-domain) filter is retired.
  const [filters, setFilters] = useState(() => ({
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    provider_id: searchParams.get("provider_id") || "all",
    campus: searchParams.get("campus") || "all",
    grade: searchParams.get("grade") || "all",
    impact_domain: (searchParams.get("impact_domain") || "").split(",").filter(Boolean),
    eligibility: (searchParams.get("eligibility") || "").split(",").filter(Boolean),
    status: searchParams.get("status") || "all",
  }));

  // Iter 37 — Auto-scope when lens is locked to "provider" and we now
  // know who the caller is. Runs once per provider load.
  useEffect(() => {
    if (lockedLens === "provider" && currentProvider?.id && filters.provider_id !== currentProvider.id) {
      setFilters((f) => ({ ...f, provider_id: currentProvider.id }));
    }
  }, [lockedLens, currentProvider, filters.provider_id]);

  // FP3-119B — Build the query params object once so it can flow
  // into both the summary + trends React Query cache keys. Filter
  // changes flip the key and trigger a new fetch; unchanged filters
  // render immediately from cache on revisit.
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();
  const summaryParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length > 0) params[k] = v.join(",");
      } else if (v && v !== "all") params[k] = v;
    });
    // FP3-053 — Forward super-admin context narrowing to both the
    // summary and trends endpoints so caseload / compliance /
    // service-delivery / coverage / trends sections all stay scoped
    // to the selected org/school. Backend silently ignores these
    // params for non-super-admin so this is a no-op for them.
    if (asOrg) params.as_org = asOrg;
    if (asSchool) params.as_school = asSchool;
    return params;
  }, [filters, asOrg, asSchool]);
  const trendParams = useMemo(() => {
    const p = { months: 6 };
    if (summaryParams.provider_id) p.provider_id = summaryParams.provider_id;
    if (summaryParams.campus) p.campus = summaryParams.campus;
    if (asOrg) p.as_org = asOrg;
    if (asSchool) p.as_school = asSchool;
    return p;
  }, [summaryParams.provider_id, summaryParams.campus, asOrg, asSchool]);

  const authScope = authScopeKey({ user });
  const queriesEnabled = !adminNoProviderSelected && !isAssistantUser;
  const summaryQuery = useQuery({
    queryKey: ["reports", "summary", authScope, summaryParams],
    queryFn: async () => (await api.getReportsSummary(summaryParams)).data,
    staleTime: STALE.REPORTS,
    enabled: queriesEnabled,
  });
  const trendsQuery = useQuery({
    queryKey: ["reports", "trends", authScope, trendParams],
    queryFn: async () => (await api.getReportsTrends(trendParams)).data,
    staleTime: STALE.REPORTS,
    enabled: queriesEnabled,
  });

  const data = summaryQuery.data || null;
  const trends = trendsQuery.data || null;
  const loading =
    queriesEnabled &&
    (summaryQuery.isPending || trendsQuery.isPending);
  const refetching =
    (summaryQuery.isFetching && !summaryQuery.isPending) ||
    (trendsQuery.isFetching && !trendsQuery.isPending);
  const reportsUpdatedAt = Math.max(
    summaryQuery.dataUpdatedAt || 0,
    trendsQuery.dataUpdatedAt || 0,
  );
  const error = useMemo(() => {
    const e = summaryQuery.error || trendsQuery.error;
    if (!e) return "";
    const detail = e?.response?.data?.detail || e?.message || "Could not load reports.";
    return typeof detail === "string" ? detail : "Could not load reports.";
  }, [summaryQuery.error, trendsQuery.error]);
  const refetchReports = () => {
    summaryQuery.refetch();
    trendsQuery.refetch();
  };

  // Sync URL (filters + lens). FP3-053 — Preserve `as_org` /
  // `as_school` (and `tab`) so navigating to Reports from the
  // sidebar context selector doesn't silently reset the platform-
  // owner's view back to "All Organizations".
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length > 0) next.set(k, v.join(","));
      } else if (v && v !== "all") next.set(k, v);
    });
    if (lens !== "admin") next.set("lens", lens);
    if (asProviderParam) next.set("as_provider", asProviderParam);
    if (asOrg) next.set("as_org", asOrg);
    if (asSchool) next.set("as_school", asSchool);
    const currentTab = searchParams.get("tab");
    if (currentTab) next.set("tab", currentTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, lens, adminNoProviderSelected]);

  // ── Lens transitions: auto-scope provider filter ─────────────────────
  // Admin lens clears the provider filter (shows everything).
  // Provider lens locks it to current provider.
  const switchLens = (next) => {
    if (next === lens) return;
    setLens(next);
    if (next === "provider" && currentProvider?.id) {
      setFilters((f) => ({ ...f, provider_id: currentProvider.id }));
    } else if (next === "admin") {
      setFilters((f) => ({ ...f, provider_id: "all" }));
    }
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const ref = data?.ref || { providers: [], impact_domains: [], eligibility_categories: [] };
  // FP3-086 — chip options for the two multi-select taxonomy rows.
  const eligibilityChipOptions = useMemo(() => ([
    ...ref.eligibility_categories.map((c) => ({
      value: c.key,
      label: c.abbr || c.label,
      title: c.label,
    })),
    { value: "unset", label: "Unset", title: "No eligibility set on either field" },
  ]), [ref.eligibility_categories]);
  const impactChipOptions = useMemo(() => (
    (ref.impact_domains && ref.impact_domains.length > 0
      ? ref.impact_domains
      : Object.entries(IMPACT_LABELS).map(([key, label]) => ({ key, label }))
    ).map((d) => ({ value: d.key, label: d.label }))
  ), [ref.impact_domains]);
  const campuses = useMemo(() => {
    const s = new Set(ref.providers.map((p) => p.campus).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [ref.providers]);
  const grades = useMemo(() => {
    const s = new Set((data?.caseload?.by_grade || []).map((r) => r.grade).filter((g) => g && g !== "—"));
    return ["all", ...Array.from(s)];
  }, [data]);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => {
    if (Array.isArray(v)) return v.length > 0;
    if (k === "start_date" || k === "end_date") return !!v;
    return v && v !== "all";
  });

  // Iter 41 — Tabs replace the long-scroll section list. Filters live
  // ABOVE the tabs and persist when switching — same component state, so
  // changing tabs is purely visual. URL `?tab=trends` is honored on
  // mount and synced on change so deep-links stay shareable.
  // FP3-111A — assistants only see the Assigned Minutes tab (the
  // other sections 403 on their identity).
  const tabKeys = isAssistantUser
    ? ["assigned-minutes"]
    : lens === "admin"
    ? ["overview", "assigned-minutes", "compliance", "service-delivery", "coverage", "trends"]
    : ["overview", "assigned-minutes", "compliance", "service-delivery", "trends"];
  const initialTab = (() => {
    if (isAssistantUser) return "assigned-minutes";
    const t = searchParams.get("tab");
    if (t && tabKeys.includes(t)) return t;
    // Backward-compat: legacy hash anchors → tab keys
    const hash = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
    const hashMap = { caseload: "overview", compliance: "compliance", "service-delivery": "service-delivery", coverage: "coverage", trends: "trends" };
    if (hash && hashMap[hash] && tabKeys.includes(hashMap[hash])) return hashMap[hash];
    return "overview";
  })();
  const [tab, setTabState] = useState(initialTab);
  const setTab = (next) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    if (next && next !== "overview") params.set("tab", next); else params.delete("tab");
    setSearchParams(params, { replace: true });
  };
  // If lens flips and current tab no longer exists (Coverage on
  // provider lens), fall back to Overview.
  useEffect(() => {
    if (!tabKeys.includes(tab)) setTabState("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens]);

  // Iter 41 — Compact filter bar. Date range + Provider stay always
  // visible; the rest collapse behind a "More filters" toggle.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedActiveCount =
    ["campus", "grade", "status"].filter((k) => filters[k] && filters[k] !== "all").length +
    (filters.eligibility.length > 0 ? 1 : 0) +
    (filters.impact_domain.length > 0 ? 1 : 0);

  // Pretty labels for active-filter chips.
  const filterChipLabel = (key, value) => {
    if (key === "start_date" || key === "end_date") return `${key === "start_date" ? "From" : "To"} ${value}`;
    if (key === "provider_id") {
      const p = ref.providers.find((x) => x.id === value);
      return p ? `Provider · ${p.name}` : `Provider · ${value}`;
    }
    if (key === "campus") return `Campus · ${value}`;
    if (key === "grade") return `Grade · ${value}`;
    if (key === "impact_domain") {
      const labels = (Array.isArray(value) ? value : [value]).map(
        (v) => IMPACT_LABELS[v] || v
      );
      return `Areas of Impact · ${labels.join(", ")}`;
    }
    if (key === "eligibility") {
      const labels = (Array.isArray(value) ? value : [value]).map((v) => {
        if (v === "unset") return "Unset";
        const c = ref.eligibility_categories.find((x) => x.key === v);
        return c ? (c.abbr || c.label) : v;
      });
      return `Eligibility · ${labels.join(", ")}`;
    }
    if (key === "status") return `Status · ${STATUS_LABELS[value] || value}`;
    return `${key} · ${value}`;
  };
  const clearOne = (key) => {
    if (key === "start_date" || key === "end_date") setFilter(key, "");
    else if (Array.isArray(filters[key])) setFilter(key, []);
    else setFilter(key, "all");
  };

  // FP3-029B5 — When an admin lands on /reports (provider lens)
  // WITHOUT explicitly opening a specific provider's read-only
  // workspace, render a calm empty-state. This prevents the legacy
  // fall-through to Sarah Mitchell's My Reports.
  if (adminNoProviderSelected) {
    return (
      <PageShell testId="reports-page-admin-no-provider" variant="compact" className="animate-none">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight">
            Reports
          </h1>
          <p className="text-[12px] text-[#6B7280] mt-1">
            School-wide oversight
          </p>
        </div>
        <Card className="border-[#E5E3DB] rounded-2xl">
          <CardContent className="p-8 text-center text-[#3F4756]">
            <UserCircle size={28} weight="duotone" className="text-[#3F4756] mx-auto mb-3" />
            <h3 className="text-[14px] font-semibold heading-font mb-1">
              Pick a provider to view their reports
            </h3>
            <p className="text-[12px] text-[#6B7280] max-w-md mx-auto leading-relaxed">
              Provider Reports are scoped to a single provider. Open a
              provider from the Providers admin page and click{" "}
              <em>View provider reports</em>, or use the Admin Reports
              page for school-wide oversight.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Link
                to="/admin/providers"
                data-testid="reports-pick-provider"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-[#1E3F33] bg-[#E8F0EB] hover:bg-[#d5e5d9] px-3 py-1.5 rounded-md"
              >
                <Users size={12} weight="duotone" /> Open Providers
              </Link>
              <Link
                to="/admin/reports"
                data-testid="reports-go-admin"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-[#3F4756] bg-[#F1F2F4] hover:bg-[#E5E3DB] px-3 py-1.5 rounded-md"
              >
                <Buildings size={12} weight="duotone" /> Admin Reports
              </Link>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell testId="reports-page" variant="compact" className="animate-none">
      {/* Page header + Lens toggle — FP3-112F: title/description sit
          on top; the Admin/Provider lens toggle drops to its own row
          directly BELOW the title block (natural left-aligned).
          Dropped `justify-between` so the toggle never floats at the
          far-right edge of the bounded 1280px shell. */}
      <div className="mb-5" data-testid="reports-header">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight" data-testid="reports-title">
              {lens === "provider" ? "My Reports" : "Reports"}
            </h1>
            {/* FP3-053 — Compact context pill mirrors the AdminShell
                pill on other admin pages. Renders only when a
                super admin has pinned an org via the sidebar
                context selector. */}
            <ReportsContextPill />
          </div>
          <p className="text-[12px] text-[#6B7280] mt-1">
            {lens === "provider"
              ? <>Personal practice oversight · {currentProvider ? `${currentProvider.first_name} ${currentProvider.last_name}` : "—"}</>
              : <>School-wide oversight · {data ? <>as of {fmtDate(data.as_of)}</> : "loading…"}</>
            }
          </p>
          {lens === "admin" && isSuperAdminUser && <SnapshotStatusBar />}
        </div>
        {lens === "admin" && !isAssistantUser && !adminNoProviderSelected && (
          <div className="mt-3 flex items-center">
            <RefreshChip
              dataUpdatedAt={reportsUpdatedAt || null}
              isFetching={refetching}
              onRefresh={refetchReports}
              testid="reports-refresh"
            />
          </div>
        )}
        {!lockedLens && (
          <div
            data-testid="reports-lens-toggle"
            role="tablist"
            className="mt-3 inline-flex bg-[#F3F1EC] rounded-lg p-1 border border-[#E5E3DB]"
          >
            <button
              data-testid="lens-admin"
              role="tab"
              aria-selected={lens === "admin"}
              onClick={() => switchLens("admin")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                lens === "admin"
                  ? "bg-white text-[#3F4756] shadow-sm"
                  : "text-[#6B7280] hover:text-[#1F2937]"
              }`}
            >
              <Buildings size={13} weight={lens === "admin" ? "duotone" : "regular"} /> Admin
              <span className="text-[10px] text-[#9B9890]">school-wide</span>
            </button>
          <button
            data-testid="lens-provider"
            role="tab"
            aria-selected={lens === "provider"}
            onClick={() => switchLens("provider")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
              lens === "provider"
                ? "bg-white text-[#1E3F33] shadow-sm"
                : "text-[#6B7280] hover:text-[#1F2937]"
            }`}
          >
            <UserCircle size={13} weight={lens === "provider" ? "duotone" : "regular"} /> Provider
            <span className="text-[10px] text-[#9B9890]">my caseload</span>
          </button>
          </div>
        )}
      </div>

      {/* Iter 41 — Compact filter bar. Date range + Provider stay
          inline; rest collapse behind "More filters". Active selections
          surface as removable chips below. Filters persist across tabs. */}
      <div
        className="mb-4 rounded-xl bg-white/70 border border-[#E8E4D8] shadow-[0_1px_0_rgba(0,0,0,0.02)]"
        data-testid="reports-filters"
      >
        <div className="flex items-center gap-2.5 flex-wrap px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold shrink-0">
            <Funnel size={12} weight="duotone" /> Filters
          </div>
          <Input
            data-testid="filter-start-date"
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilter("start_date", e.target.value)}
            className="h-7 text-[11px] w-32 bg-white border-[#E5E3DB]"
          />
          <span className="text-[#9B9890] text-[10px]">→</span>
          <Input
            data-testid="filter-end-date"
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilter("end_date", e.target.value)}
            className="h-7 text-[11px] w-32 bg-white border-[#E5E3DB]"
          />
          {lens === "admin" && (
            <FilterSelect testid="filter-provider" label="Provider" value={filters.provider_id} onChange={(v) => setFilter("provider_id", v)}>
              <option value="all">All providers</option>
              {ref.providers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role || "—"})</option>)}
            </FilterSelect>
          )}
          <button
            data-testid="filter-more-toggle"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-[#3F4756] hover:text-[#1E3F33] px-2 py-1 rounded-md hover:bg-[#F3F1EC]"
          >
            {advancedOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            More filters
            {advancedActiveCount > 0 && (
              <span className="ml-0.5 bg-[#3F4756] text-white text-[9px] tabular-nums rounded-full px-1.5 py-0.5 leading-none">
                {advancedActiveCount}
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button
              data-testid="filter-clear"
              onClick={() => setFilters({ start_date: "", end_date: "", provider_id: lens === "provider" ? filters.provider_id : "all", campus: "all", grade: "all", impact_domain: [], eligibility: [], status: "all" })}
              className="text-[11px] text-[#6B7280] hover:text-[#3F4756] underline"
            >
              Clear
            </button>
          )}
        </div>
        {advancedOpen && (
          <div className="px-3 pb-3 pt-1 border-t border-[#ECEAE4] space-y-2" data-testid="reports-filters-advanced">
            <div className="flex items-center gap-2.5 flex-wrap">
              <FilterSelect testid="filter-campus" label="Campus" value={filters.campus} onChange={(v) => setFilter("campus", v)}>
                {campuses.map((c) => <option key={c} value={c}>{c === "all" ? "All campuses" : c}</option>)}
              </FilterSelect>
              <FilterSelect testid="filter-grade" label="Grade" value={filters.grade} onChange={(v) => setFilter("grade", v)}>
                {grades.map((g) => <option key={g} value={g}>{g === "all" ? "All grades" : g}</option>)}
              </FilterSelect>
              <FilterSelect testid="filter-status" label="Status" value={filters.status} onChange={(v) => setFilter("status", v)}>
                <option value="all">All statuses</option>
                {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </FilterSelect>
            </div>
            {/* FP3-086 — Eligibility + Areas of Impact multi-select
                chip rows. Same taxonomy + semantics as the Students
                page: ANY-match within a row, AND across the rows. */}
            <MultiFilterChips
              testidPrefix="filter-eligibility"
              label="Eligibility"
              options={eligibilityChipOptions}
              value={filters.eligibility}
              onChange={(v) => setFilter("eligibility", v)}
            />
            <MultiFilterChips
              testidPrefix="filter-impact"
              label="Areas of Impact"
              options={impactChipOptions}
              value={filters.impact_domain}
              onChange={(v) => setFilter("impact_domain", v)}
            />
          </div>
        )}
        {/* Active filter chips — present on the bar regardless of
            advanced expansion. Only render values the user has set. */}
        {hasActiveFilters && (
          <div
            className="flex items-center gap-1.5 flex-wrap px-3 pb-2.5 pt-1 border-t border-[#ECEAE4]"
            data-testid="reports-filter-chips"
          >
            {Object.entries(filters).map(([k, v]) => {
              const active = Array.isArray(v)
                ? v.length > 0
                : (k === "start_date" || k === "end_date") ? !!v : v && v !== "all";
              if (!active) return null;
              // Provider chip is non-removable on provider lens
              const locked = lens === "provider" && k === "provider_id";
              return (
                <span
                  key={k}
                  data-testid={`filter-chip-${k}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    locked ? "bg-[#1E3F33]/10 text-[#1E3F33]" : "bg-[#3F4756]/10 text-[#3F4756]"
                  }`}
                >
                  {filterChipLabel(k, v)}
                  {!locked && (
                    <button
                      data-testid={`filter-chip-remove-${k}`}
                      onClick={() => clearOne(k)}
                      className="hover:text-[#B85C5C]"
                      aria-label={`Remove filter ${k}`}
                    >
                      <X size={10} weight="bold" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter 41 — Tabs replace the long-scroll layout. Filters above
          persist across tabs. Tab key syncs to URL. */}
      {/* FP3-060 — Error branch. Surfaces 4xx/5xx detail with a
          retry affordance instead of hanging on the skeleton. */}
      {error && !loading && !data ? (
        <Card
          data-testid="reports-error-card"
          className="border-[#F4D1D1] bg-[#FBEFE8]"
        >
          <CardContent className="p-6 flex items-start gap-3">
            <Warning
              size={18}
              weight="duotone"
              className="text-[#A8453A] shrink-0 mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <p
                data-testid="reports-error-detail"
                className="text-[13px] text-[#7A3A2C] font-semibold"
              >
                Reports could not load
              </p>
              <p className="text-[12.5px] text-[#7A3A2C] mt-1 leading-relaxed">
                {error}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={refetchReports}
                data-testid="reports-error-retry"
                className="mt-3 bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : loading || (!isAssistantUser && !data) ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} data-testid="reports-tabs">
          <TabsList
            data-testid="reports-tabs-list"
            className="bg-[#F3F1EC] border border-[#E5E3DB] h-auto p-1 mb-5 flex-wrap"
          >
            {!isAssistantUser && (
              <TabsTrigger value="overview" data-testid="reports-tab-overview" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
                <Users size={13} weight="duotone" /> Overview
              </TabsTrigger>
            )}
            <TabsTrigger value="assigned-minutes" data-testid="reports-tab-assigned-minutes" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
              <Clock size={13} weight="duotone" /> Assigned Minutes
            </TabsTrigger>
            {!isAssistantUser && (
              <TabsTrigger value="compliance" data-testid="reports-tab-compliance" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
                <ClipboardText size={13} weight="duotone" /> Compliance
              </TabsTrigger>
            )}
            {!isAssistantUser && (
              <TabsTrigger value="service-delivery" data-testid="reports-tab-service-delivery" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
                <Sparkle size={13} weight="duotone" /> Service Delivery
              </TabsTrigger>
            )}
            {lens === "admin" && !isAssistantUser && (
              <TabsTrigger value="coverage" data-testid="reports-tab-coverage" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
                <Heartbeat size={13} weight="duotone" /> Coverage
              </TabsTrigger>
            )}
            {!isAssistantUser && (
              <TabsTrigger value="trends" data-testid="reports-tab-trends" className="text-[12px] gap-1.5 data-[state=active]:bg-white data-[state=active]:text-[#1E3F33]">
                <TrendUp size={13} weight="duotone" /> Trends
              </TabsTrigger>
            )}
          </TabsList>

          {!isAssistantUser && data && (
            <TabsContent value="overview" className="mt-0" data-testid="reports-tabpanel-overview">
              <CaseloadSection caseload={data.caseload} lens={lens} />
            </TabsContent>
          )}
          <TabsContent value="assigned-minutes" className="mt-0" data-testid="reports-tabpanel-assigned-minutes">
            <AssignedMinutesSection filters={filters} lens={lens} />
          </TabsContent>
          {!isAssistantUser && data && (
            <TabsContent value="compliance" className="mt-0" data-testid="reports-tabpanel-compliance">
              <ComplianceSection compliance={data.compliance} lens={lens} />
            </TabsContent>
          )}
          {!isAssistantUser && data && (
            <TabsContent value="service-delivery" className="mt-0" data-testid="reports-tabpanel-service-delivery">
            {lens === "provider" && !isFidelityOff && (filters.provider_id !== "all" ? filters.provider_id : currentProvider?.id) && (
              <div className="mb-6" data-testid="reports-provider-fidelity-card">
                <ProviderFidelityReport
                  providerId={filters.provider_id !== "all" ? filters.provider_id : currentProvider?.id}
                />
              </div>
            )}
            <ServiceDeliverySection sd={data.service_delivery} lens={lens} />
          </TabsContent>
          )}
          {lens === "admin" && !isAssistantUser && data && (
            <TabsContent value="coverage" className="mt-0" data-testid="reports-tabpanel-coverage">
              <CoverageSection sc={data.schedule_coverage} />
            </TabsContent>
          )}
          {!isAssistantUser && (
            <TabsContent value="trends" className="mt-0" data-testid="reports-tabpanel-trends">
              {trends ? <TrendsSection trends={trends} lens={lens} /> : <Skeleton className="h-48" />}
            </TabsContent>
          )}
        </Tabs>
      )}
    </PageShell>
  );
}

function FilterSelect({ testid, label, value, onChange, children }) {
  const isAll = !value || value === "all";
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-0.5 transition-colors ${
      isAll ? "bg-white/70 border border-transparent" : "bg-white border border-[#3F4756]/15 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
    }`}>
      <span className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 text-[11px] rounded-full bg-transparent pl-1 pr-5 text-[#1F2937] focus:outline-none focus:ring-1 focus:ring-[#3F4756]/30 cursor-pointer"
      >
        {children}
      </select>
    </div>
  );
}

// FP3-086 — Multi-select chip row (ANY-match within the row). Mirrors
// the Students page MultiFilterPills pattern.
function MultiFilterChips({ testidPrefix, label, options, value = [], onChange }) {
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testidPrefix}-row`}>
      <span className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">{label}</span>
      <div className="flex bg-[#F3F1EC] rounded-lg p-0.5 flex-wrap">
        <button
          data-testid={`${testidPrefix}-all`}
          onClick={() => onChange([])}
          className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium ${
            value.length === 0 ? "bg-white text-[#3F4756] shadow-sm" : "text-[#6B7280]"
          }`}
        >
          All
        </button>
        {options.map((opt) => {
          const active = value.includes(opt.value);
          return (
            <button
              key={opt.value}
              data-testid={`${testidPrefix}-${opt.value}`}
              onClick={() => toggle(opt.value)}
              title={opt.title || opt.label}
              className={`px-2 py-0.5 rounded-md text-[10.5px] font-medium transition-colors ${
                active ? "bg-[#1E3F33] text-white shadow-sm" : "text-[#6B7280] hover:bg-white/60"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Section 1: Caseload Overview ─────────────────────────────────────────
function CaseloadSection({ caseload, lens }) {
  const maxProvider = Math.max(1, ...caseload.by_provider.map((r) => r.count));
  const maxGrade = Math.max(1, ...caseload.by_grade.map((r) => r.count));
  const maxImpact = Math.max(1, ...(caseload.by_impact_domain || []).map((r) => r.count));
  const maxElig = Math.max(1, ...caseload.by_eligibility.map((r) => r.count));

  const isProvider = lens === "provider";
  return (
    <Section
      title={isProvider ? "My Caseload" : "Caseload Overview"}
      subtitle={isProvider ? "Your students with IEPs" : "Who you serve right now"}
      icon={Users}
      id="caseload"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile testid="metric-active-students" label="Students with IEPs" value={caseload.active_students} accent="slate" />
        <MetricTile testid="metric-recently-added" label="Added · last 30d" value={caseload.recently_added_30d} sub={isProvider ? "" : `across ${caseload.by_provider.length} provider${caseload.by_provider.length === 1 ? "" : "s"}`} />
        <MetricTile testid="metric-recently-exited" label="Exited · last 30d" value={caseload.recently_exited_30d} />
        <MetricTile testid="metric-campuses" label="Campuses" value={caseload.by_campus.length} sub={caseload.by_campus.map((c) => c.campus).filter((c) => c !== "—").join(" · ") || "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        {!isProvider && (
          <ReportCard
            title="Students by assigned provider"
            subtitle={`${caseload.by_provider.length} provider${caseload.by_provider.length === 1 ? "" : "s"} · students with multiple providers are counted on each provider's row`}
            id="caseload-by-provider"
          >
            {caseload.by_provider.length === 0 ? (
              <p className="text-[11px] text-[#6B7280] italic py-2">No providers match.</p>
            ) : (
              <div className="space-y-1.5">
                {caseload.by_provider.map((r) => (
                  <HBar key={r.provider_id} label={`${r.name}${r.role ? ` · ${r.role}` : ""}`} count={r.count} max={maxProvider} tone="bg-[#1E3F33]" />
                ))}
              </div>
            )}
          </ReportCard>
        )}

        <ReportCard title={isProvider ? "My students by grade" : "Students by grade"} id="caseload-by-grade">
          {caseload.by_grade.length === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No grades.</p>
          ) : (
            <div className="space-y-1.5">
              {caseload.by_grade.map((r) => (
                <HBar key={r.grade} label={`Grade ${r.grade}`} count={r.count} max={maxGrade} tone="bg-[#3F4756]" />
              ))}
            </div>
          )}
        </ReportCard>

        <ReportCard
          title={isProvider ? "My students by Areas of Impact" : "Students by Areas of Impact"}
          subtitle={isProvider ? "One student can span multiple areas" : "From each student's Areas of Impact selections · a student can appear in more than one area"}
          id="caseload-by-impact"
        >
          {(caseload.by_impact_domain || []).length === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No Areas of Impact selected yet.</p>
          ) : (
            <div className="space-y-1.5">
              {(caseload.by_impact_domain || []).map((r) => (
                <HBar key={r.key} label={r.label} count={r.count} max={maxImpact} tone={DOMAIN_TONE[r.key] || "bg-[#3F4756]"} />
              ))}
            </div>
          )}
        </ReportCard>

        <ReportCard title={isProvider ? "My students by eligibility" : "Students by eligibility"} subtitle="Arizona categories" id="caseload-by-eligibility">
          {caseload.by_eligibility.length === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No eligibility data.</p>
          ) : (
            <div className="space-y-1.5">
              {caseload.by_eligibility.map((r) => (
                <HBar
                  key={r.label}
                  label={r.abbr ? `${r.abbr} · ${r.label}` : r.label}
                  count={r.count}
                  max={maxElig}
                  tone={r.label === "Unset" ? "bg-[#9B9890]" : "bg-[#4A5C7A]"}
                />
              ))}
            </div>
          )}
        </ReportCard>

        {/* FP3-099 — Attendance Pattern distribution. Canonical
            student.attendance_pattern only (same normalizer as Admin
            Profile / Provider Demographics); never inferred from
            teacher text. Each student counted exactly once. */}
        {!isProvider && caseload.by_attendance_pattern && (
          <ReportCard
            title="Attendance Pattern"
            subtitle={`${caseload.by_attendance_pattern.total} student${caseload.by_attendance_pattern.total === 1 ? "" : "s"} represented · each counted once`}
            id="caseload-by-attendance"
          >
            {caseload.by_attendance_pattern.total === 0 ? (
              <p className="text-[11px] text-[#6B7280] italic py-2">No students match.</p>
            ) : (
              <div className="space-y-1.5" data-testid="report-attendance-pattern">
                {caseload.by_attendance_pattern.buckets.map((r) => (
                  <div key={r.key} className="flex items-center gap-2" data-testid={`attendance-row-${r.key}`}>
                    <div className="flex-1 min-w-0">
                      <HBar
                        label={r.label}
                        count={r.count}
                        max={Math.max(1, ...caseload.by_attendance_pattern.buckets.map((x) => x.count))}
                        tone={r.key === "unset" ? "bg-[#9B9890]" : "bg-[#3D6B57]"}
                      />
                    </div>
                    <span className="text-[10px] text-[#6B7280] tabular-nums w-11 text-right shrink-0">
                      {r.pct}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ReportCard>
        )}
      </div>
    </Section>
  );
}

// ── Section 1b (FP3-111): Assigned Service Minutes ──────────────────────
function AssignedMinutesSection({ filters, lens }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    const params = {};
    if (filters.provider_id && filters.provider_id !== "all") params.provider_id = filters.provider_id;
    if (filters.campus && filters.campus !== "all") params.campus = filters.campus;
    if (filters.grade && filters.grade !== "all") params.grade = filters.grade;
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.eligibility?.length) params.eligibility = filters.eligibility.join(",");
    if (filters.impact_domain?.length) params.impact_domain = filters.impact_domain.join(",");
    if (filters.as_org) params.as_org = filters.as_org;
    if (filters.as_school) params.as_school = filters.as_school;
    api.reportsAssignedMinutes(params).then(
      (r) => { if (!cancelled) { setData(r.data); setLoading(false); } },
      (e) => { if (!cancelled) { setErr(prettyApiErrorLocal(e)); setLoading(false); } },
    );
    return () => { cancelled = true; };
  }, [filters.provider_id, filters.campus, filters.grade, filters.status,
      filters.eligibility, filters.impact_domain, filters.as_org, filters.as_school]);

  if (loading) return <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-24" /></div>;
  if (err) return (
    <Section title="Assigned Service Minutes" subtitle="Committed minutes/month" icon={Clock} id="assigned-minutes">
      <Card className="border-[#E5E3DB]"><CardContent className="p-6 text-[13px] text-[#B00020]">{err}</CardContent></Card>
    </Section>
  );
  if (!data) return null;
  const solo = data.solo_view;
  const team = data.team_view;
  const org = data.org_view;
  // FP3-111A — Assistant scope: only 2 cards (my minutes, my students).
  // No team totals, no other-provider rows.
  const isAssistantScope = data.scope === "assistant";
  // FP3-117 — Read-only ledger download. Same scope filters as the
  // report itself, so a provider gets their own team, an assistant
  // gets their own scope, a super admin drilling by provider_id gets
  // that provider's team.
  const ledgerCsvUrl = api.reportsAssignedMinutesLedgerCsvUrl({
    provider_id: filters.provider_id && filters.provider_id !== "all" ? filters.provider_id : "",
    campus: filters.campus && filters.campus !== "all" ? filters.campus : "",
    grade: filters.grade && filters.grade !== "all" ? filters.grade : "",
    status: filters.status && filters.status !== "all" ? filters.status : "",
    eligibility: filters.eligibility?.length ? filters.eligibility.join(",") : "",
    impact_domain: filters.impact_domain?.length ? filters.impact_domain.join(",") : "",
    as_org: filters.as_org || "",
    as_school: filters.as_school || "",
  });
  return (
    <Section
      title="Assigned Service Minutes"
      subtitle="Committed minutes/month · never delivered/scheduled"
      icon={Clock}
      id="assigned-minutes"
      action={
        <a
          href={ledgerCsvUrl}
          data-testid="assigned-minutes-ledger-download"
          className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[#3F4756] hover:text-[#1F2937] border border-[#E5E3DB] bg-white hover:bg-[#F5F3EE] rounded-full px-2.5 py-1 transition-colors"
          title="Download a row-by-row contribution ledger (CSV). Read-only diagnostic (FP3-117)."
        >
          <ClipboardText size={13} weight="duotone" />
          Download Ledger
        </a>
      }
    >
      {isAssistantScope && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="assigned-minutes-assistant">
          <SummaryCard
            testid="assigned-minutes-assistant-mine-total"
            label="My Assigned Minutes / Month"
            value={fmtMinutes(data.total_assigned_minutes_month)}
            hint={
              data.service_provider?.display_name
                ? `${data.service_provider.display_name} · ${data.service_provider.professional_type} · ${data.service_provider.relationship_role}`
                : ""
            }
          />
          <SummaryCard
            testid="assigned-minutes-assistant-mine-students"
            label="My Students With Assigned Minutes"
            value={Number(data.students_with_assigned_minutes || 0).toLocaleString()}
            hint="Students you personally service under supervision"
          />
        </div>
      )}
      {solo && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="assigned-minutes-solo">
          <SummaryCard
            testid="assigned-minutes-solo-total"
            label="Total Assigned Minutes / Month"
            value={fmtMinutes(solo.total_minutes_per_month)}
            hint={solo.provider?.name ? `${solo.provider.name} · ${solo.provider.role_label}` : ""}
          />
          <SummaryCard
            testid="assigned-minutes-solo-students"
            label="Total Students With Assigned Minutes"
            value={solo.total_students.toLocaleString()}
            hint="Currently on your direct-service caseload"
          />
        </div>
      )}
      {team && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="assigned-minutes-team-summary">
            <SummaryCard
              testid="assigned-minutes-team-total"
              label="Team Total Assigned Minutes / Month"
              value={fmtMinutes(team.team_total_minutes)}
              hint="Provider direct + assistants"
              accent="team"
            />
            <SummaryCard
              testid="assigned-minutes-provider-direct"
              label="Provider Direct-Service Minutes / Month"
              value={fmtMinutes(team.provider_direct_minutes)}
              hint="Students you personally service"
            />
            <SummaryCard
              testid="assigned-minutes-assistant-total"
              label="Assistant Assigned Minutes / Month"
              value={fmtMinutes(team.assistant_total_minutes)}
              hint={`${team.breakdown.filter((r) => !r.is_supervisor && r.students > 0).length} active assistant${team.breakdown.filter((r) => !r.is_supervisor && r.students > 0).length === 1 ? "" : "s"}`}
            />
          </div>
          <BreakdownTable rows={team.breakdown} testid="assigned-minutes-breakdown" />
        </>
      )}
      {org && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="assigned-minutes-org-summary">
            <SummaryCard
              testid="assigned-minutes-org-total"
              label="Organization Total Assigned Minutes / Month"
              value={fmtMinutes(org.organization_total_minutes)}
              hint={`${org.teams.length} supervising team${org.teams.length === 1 ? "" : "s"}`}
              accent="team"
            />
          </div>
          <div className="space-y-3">
            {org.teams.map((snap) => (
              <div key={snap.supervising_provider_id} className="bg-white border border-[#E5E3DB] rounded-2xl p-4"
                data-testid={`assigned-minutes-team-${snap.supervising_provider_id}`}>
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F1F2F4]">
                  <span className="text-[13px] font-semibold text-[#1F2937] heading-font">
                    {snap.breakdown[0]?.name || "Team"} · {snap.breakdown[0]?.role_label || ""}
                  </span>
                  <span className="text-[12px] text-[#6B7280]">
                    Team total: <span className="font-semibold text-[#1F2937]">{fmtMinutes(snap.team_total_minutes)}</span>
                  </span>
                </div>
                <BreakdownTable rows={snap.breakdown} testid={`assigned-minutes-breakdown-${snap.supervising_provider_id}`} compact />
              </div>
            ))}
          </div>
        </>
      )}
      {(data.unresolved_count || data.unresolved_students?.length || 0) > 0 && (
        <Card className="border-[#E8D5A8] bg-[#FDF7ED]" data-testid="assigned-minutes-unresolved">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Warning size={16} weight="duotone" className="text-[#8A6B33] mt-0.5" />
              <div className="text-[12px] text-[#5A4A20]">
                <p className="font-semibold mb-1" data-testid="assigned-minutes-unresolved-headline">
                  {data.unresolved_count || data.unresolved_students.length} student{(data.unresolved_count || data.unresolved_students.length) === 1 ? "" : "s"} could not be attributed
                  {" · "}
                  <span data-testid="assigned-minutes-unresolved-minutes">
                    {fmtMinutes(data.unresolved_minutes || 0)}
                  </span>{" "}
                  <span className="font-normal">not shown in totals above</span>
                </p>
                <p className="text-[11.5px] leading-snug">
                  These students have more than one active assistant assignment
                  in the same supervision group. They are counted once (never
                  double-counted) but not attributed to any single provider or
                  assistant, and their minutes are NOT rolled into the team /
                  provider / assistant totals above — the totals are therefore
                  NOT presented as silently complete. Resolve by keeping only
                  one active assistant per student on the Caseload.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {!solo && !team && !org && !isAssistantScope && (
        <Card className="border-[#E5E3DB]"><CardContent className="p-6 text-[12px] text-[#6B7280]">No assigned minutes to display for the current scope.</CardContent></Card>
      )}
    </Section>
  );
}

function SummaryCard({ testid, label, value, hint, accent }) {
  const ring = accent === "team" ? "ring-1 ring-[#1E3F33]/15 bg-[#F0F5F1]" : "bg-white";
  return (
    <div
      data-testid={testid}
      className={`${ring} border border-[#E5E3DB] rounded-2xl px-4 py-3`}
    >
      <p className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-[#6B7280]">{label}</p>
      <p className="text-2xl font-semibold text-[#1F2937] mt-1 tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-[#6B7280] mt-0.5">{hint}</p>}
    </div>
  );
}

function BreakdownTable({ rows, testid, compact = false }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border border-[#E5E3DB] rounded-2xl overflow-hidden ${compact ? "" : "mt-1"}`}
    >
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#F5F3EE] text-[#6B7280]">
          <tr>
            <th className="text-left px-4 py-2 font-semibold uppercase tracking-[0.06em] text-[10.5px]">Service Provider</th>
            <th className="text-left px-4 py-2 font-semibold uppercase tracking-[0.06em] text-[10.5px]">Role</th>
            <th className="text-right px-4 py-2 font-semibold uppercase tracking-[0.06em] text-[10.5px]">Students</th>
            <th className="text-right px-4 py-2 font-semibold uppercase tracking-[0.06em] text-[10.5px]">Assigned Minutes / Month</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F1F2F4]">
          {rows.map((r) => (
            <tr
              key={r.provider_id}
              data-testid={`assigned-minutes-row-${r.provider_id}`}
              className={r.is_supervisor ? "bg-[#F9FAF6]" : ""}
            >
              <td className="px-4 py-2.5 text-[#1F2937] font-medium">{r.name}</td>
              <td className="px-4 py-2.5 text-[#6B7280]">{r.role_label}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-[#1F2937]">{r.students.toLocaleString()}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#1F2937]">{fmtMinutes(r.minutes_per_month)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtMinutes(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: n % 1 === 0 ? 0 : 1 })} min/mo`;
}

function prettyApiErrorLocal(e) {
  const s = e?.response?.status;
  const d = e?.response?.data?.detail;
  if (s === 403) return typeof d === "string" ? d : "You do not have permission to view this report.";
  if (s === 401) return "Please sign in again to view this report.";
  return typeof d === "string" ? d : "Could not load the assigned-minutes report. Try again in a moment.";
}

// ── Section 2: Compliance ────────────────────────────────────────────────
function ComplianceSection({ compliance }) {
  return (
    <Section title="Compliance & Due-date Oversight" subtitle="Point-in-time snapshot" icon={ClipboardText} id="compliance">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ReportCard title="IEP review timeline" id="compliance-iep">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <MetricTile testid="iep-overdue" label="Overdue" value={compliance.iep.overdue} accent={compliance.iep.overdue > 0 ? "red" : "default"} />
            <MetricTile testid="iep-due-30d" label="Due ≤30d" value={compliance.iep.due_30d} accent={compliance.iep.due_30d > 0 ? "amber" : "default"} />
            <MetricTile testid="iep-due-60d" label="Due ≤60d" value={compliance.iep.due_60d} />
          </div>
          {compliance.iep.rows.length > 0 ? (
            <div className="space-y-1">
              {compliance.iep.rows.slice(0, 8).map((r) => (
                <DueRow key={r.student_id + r.iep_due_date} name={r.name} grade={r.grade} campus={r.campus} date={r.iep_due_date} status={r.status} />
              ))}
              {compliance.iep.rows.length > 8 && <p className="text-[10px] text-[#9B9890] italic mt-1">+{compliance.iep.rows.length - 8} more</p>}
            </div>
          ) : (
            <p className="text-[11px] text-[#1E5A3F] py-2">All clear · no overdue or due-soon IEPs.</p>
          )}
        </ReportCard>

        <ReportCard title="MET evaluation timeline" id="compliance-met">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <MetricTile testid="met-overdue" label="Overdue" value={compliance.met.overdue} accent={compliance.met.overdue > 0 ? "red" : "default"} />
            <MetricTile testid="met-due-30d" label="Due ≤30d" value={compliance.met.due_30d} accent={compliance.met.due_30d > 0 ? "amber" : "default"} />
            <MetricTile testid="met-due-60d" label="Due ≤60d" value={compliance.met.due_60d} />
          </div>
          {compliance.met.rows.length > 0 ? (
            <div className="space-y-1">
              {compliance.met.rows.slice(0, 8).map((r) => (
                <DueRow key={r.student_id + r.met_due_date} name={r.name} grade={r.grade} campus={r.campus} date={r.met_due_date} status={r.status} />
              ))}
              {compliance.met.rows.length > 8 && <p className="text-[10px] text-[#9B9890] italic mt-1">+{compliance.met.rows.length - 8} more</p>}
            </div>
          ) : (
            <p className="text-[11px] text-[#1E5A3F] py-2">All clear · no overdue or due-soon MET evaluations.</p>
          )}
        </ReportCard>

        <ReportCard title="Data hygiene" subtitle="Missing required fields" id="compliance-hygiene" className="md:col-span-2">
          <div className="grid grid-cols-3 gap-2">
            <MetricTile testid="hygiene-dob" label="Missing DOB" value={compliance.data_hygiene.missing_dob} accent={compliance.data_hygiene.missing_dob > 0 ? "amber" : "green"} />
            <MetricTile testid="hygiene-iep" label="Missing IEP date" value={compliance.data_hygiene.missing_iep_date} accent={compliance.data_hygiene.missing_iep_date > 0 ? "amber" : "green"} />
            <MetricTile testid="hygiene-eligibility" label="Missing eligibility" value={compliance.data_hygiene.missing_eligibility} accent={compliance.data_hygiene.missing_eligibility > 0 ? "amber" : "green"} />
          </div>
        </ReportCard>
      </div>
    </Section>
  );
}

function DueRow({ name, grade, campus, date, status }) {
  const tone = status === "overdue" ? "bg-[#FBEFEC] text-[#B85C5C]" : "bg-[#FDF8F3] text-[#9C6B2E]";
  return (
    <div className="flex items-center justify-between gap-2 bg-white border border-[#ECEAE4] rounded-md px-2.5 py-1.5">
      <div className="min-w-0">
        <p className="text-[12px] font-medium text-[#1F2937] truncate">{name}</p>
        <p className="text-[10px] text-[#6B7280] truncate">Grade {grade || "—"} · {campus || "—"}</p>
      </div>
      <div className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${tone} tabular-nums shrink-0`}>
        {fmtDate(date)} · {status === "overdue" ? "overdue" : "≤30d"}
      </div>
    </div>
  );
}

// ── Section 3: Service Delivery ─────────────────────────────────────────
function ServiceDeliverySection({ sd }) {
  return (
    <Section
      title="Service Delivery"
      subtitle={`${fmtDate(sd.range.start_date)} – ${fmtDate(sd.range.end_date)} · ${sd.totals.total} session${sd.totals.total === 1 ? "" : "s"}`}
      icon={Sparkle}
      id="service-delivery"
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricTile testid="sd-total" label="Total" value={sd.totals.total} accent="slate" />
        <MetricTile testid="sd-completed" label="Completed" value={sd.totals.completed} accent="green" sub={`${fmtPct(sd.completion_rate)} completion`} />
        <MetricTile testid="sd-cancelled" label="Cancelled" value={sd.totals.cancelled} accent={sd.totals.cancelled > 0 ? "amber" : "default"} />
        <MetricTile testid="sd-missed" label="Missed" value={sd.totals.missed || 0} accent={sd.totals.missed > 0 ? "red" : "default"} />
        <MetricTile testid="sd-scheduled" label="Scheduled / open" value={(sd.totals.scheduled || 0) + (sd.totals.in_progress || 0)} />
      </div>

      <div className="grid grid-cols-1 gap-3 mt-3">
        {/* Iter 38 — Cancellations card removed from Service Delivery; the
            Trends section already carries a collapsible breakdown so we
            don't show the same data twice. Under-service risk now spans
            full width here. */}
        <ReportCard
          title="Students at under-service risk"
          subtitle={`${sd.under_service_risk.count} flagged`}
          id="sd-under-service"
        >
          {sd.under_service_risk.rows.length === 0 ? (
            <p className="text-[11px] text-[#1E5A3F] py-2">All students are on-track.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {sd.under_service_risk.rows.slice(0, 12).map((r, i) => (
                <div key={`${r.student_id}-${i}`} className="flex items-center justify-between gap-2 bg-white border border-[#ECEAE4] rounded-md px-2.5 py-1.5">
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-[#1F2937] truncate">{r.student_name || "—"}</p>
                    <p className="text-[10px] text-[#6B7280] truncate">{r.provider_name} · {KIND_LABELS[r.kind] || r.kind}</p>
                  </div>
                </div>
              ))}
              {sd.under_service_risk.rows.length > 12 && (
                <p className="text-[10px] text-[#9B9890] italic mt-1 md:col-span-2">+{sd.under_service_risk.count - 12} more</p>
              )}
            </div>
          )}
        </ReportCard>
      </div>
    </Section>
  );
}

// ── Section 4: Schedule Coverage ────────────────────────────────────────
function CoverageSection({ sc }) {
  const maxKind = Math.max(1, ...(sc.open_health_issues.by_kind || []).map((r) => r.count));
  return (
    <Section title="Schedule & Coverage" subtitle="Operational risk" icon={Heartbeat} id="coverage">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ReportCard title="Provider unavailability impact" subtitle="Next 7 days" id="cov-unavail">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <MetricTile
              testid="cov-active-unavail"
              label="Active periods"
              value={sc.provider_unavailability.active_periods}
              accent={sc.provider_unavailability.active_periods > 0 ? "amber" : "green"}
            />
            <MetricTile
              testid="cov-cancelled-week"
              label="Sessions cancelled / week"
              value={sc.provider_unavailability.sessions_cancelled_this_week}
              accent={sc.provider_unavailability.sessions_cancelled_this_week > 0 ? "amber" : "default"}
            />
          </div>
          {sc.provider_unavailability.providers.length === 0 ? (
            <p className="text-[11px] text-[#1E5A3F] py-1">All providers available next 7 days.</p>
          ) : (
            <div className="space-y-1.5">
              {sc.provider_unavailability.providers.map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-2 bg-[#FDF8F3] border border-[#9C6B2E]/20 rounded-md px-2.5 py-1.5">
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-[#1F2937] truncate">{u.provider_name || "—"}</p>
                    <p className="text-[10px] text-[#6B7280]">{u.reason} · {u.start_date} → {u.end_date}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ReportCard>

        <ReportCard title="Open health issues by kind" subtitle={`${sc.open_health_issues.total} total`} id="cov-health">
          {sc.open_health_issues.by_kind.length === 0 ? (
            <p className="text-[11px] text-[#1E5A3F] py-2">All clear · no open coverage issues.</p>
          ) : (
            <div className="space-y-1.5">
              {sc.open_health_issues.by_kind.map((r) => (
                <HBar
                  key={r.kind}
                  label={KIND_LABELS[r.kind] || r.kind}
                  count={r.count}
                  max={maxKind}
                  tone="bg-[#B85C5C]"
                />
              ))}
            </div>
          )}
        </ReportCard>
      </div>
    </Section>
  );
}


// ── Section 5: Trends ────────────────────────────────────────────────────
// Lens-aware longitudinal view. Same data shape, different framing:
//   • Admin lens   → "Trends · School-wide"
//   • Provider lens → "My Trends" (backend already scopes by provider_id
//                      when the lens locks the filter)
//
// Visual language matches the rest of the page: compact CSS bars + tiny
// inline-SVG sparklines (no chart library). Fast, PDF-export-friendly,
// consistent with HBar / MetricTile.
function monthLabel(monthKey) {
  if (!monthKey) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const m = parseInt(monthKey.slice(5, 7), 10);
  return months[m - 1] || monthKey;
}

function Sparkline({ values, tone = "#1E5A3F", height = 32 }) {
  const safe = (values || []).map((v) => (v == null ? 0 : v));
  if (safe.length === 0) return null;
  const max = Math.max(1, ...safe);
  const min = Math.min(0, ...safe);
  const w = 100;
  const n = safe.length;
  const step = n > 1 ? w / (n - 1) : 0;
  const range = Math.max(1, max - min);
  const points = safe.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const area = `${points} ${((n - 1) * step).toFixed(2)},${height} 0,${height}`;
  const lastV = safe[safe.length - 1];
  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      className="w-full block"
      style={{ height }}
      aria-hidden="true"
    >
      <polygon points={area} fill={tone} opacity="0.08" />
      <polyline
        points={points}
        fill="none"
        stroke={tone}
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={((n - 1) * step).toFixed(2)}
        cy={(height - ((lastV - min) / range) * (height - 4) - 2).toFixed(2)}
        r="1.6"
        fill={tone}
      />
    </svg>
  );
}

function MiniBars({ values, labels, tone = "bg-[#3F4756]", height = 36 }) {
  const safe = (values || []).map((v) => (v == null ? 0 : v));
  const max = Math.max(1, ...safe);
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {safe.map((v, i) => {
        const pct = (v / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
            <div className="w-full flex items-end" style={{ height: height - 12 }}>
              <div
                className={`${tone} w-full rounded-sm transition-all`}
                style={{ height: `${pct}%`, minHeight: v > 0 ? "2px" : "0" }}
                title={`${labels[i]}: ${v}`}
              />
            </div>
            <span className="text-[8px] text-[#9B9890] tabular-nums truncate w-full text-center">
              {monthLabel(labels[i])}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PairedBars({ a, b, labels, toneA = "bg-[#1E5A3F]", toneB = "bg-[#9C6B2E]", height = 36 }) {
  const safeA = (a || []).map((v) => v || 0);
  const safeB = (b || []).map((v) => v || 0);
  const max = Math.max(1, ...safeA, ...safeB);
  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {labels.map((lab, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
          <div className="w-full flex items-end gap-[2px]" style={{ height: height - 12 }}>
            <div
              className={`${toneA} w-1/2 rounded-sm`}
              style={{ height: `${(safeA[i] / max) * 100}%`, minHeight: safeA[i] > 0 ? "2px" : "0" }}
              title={`${lab} added: ${safeA[i]}`}
            />
            <div
              className={`${toneB} w-1/2 rounded-sm`}
              style={{ height: `${(safeB[i] / max) * 100}%`, minHeight: safeB[i] > 0 ? "2px" : "0" }}
              title={`${lab} exited: ${safeB[i]}`}
            />
          </div>
          <span className="text-[8px] text-[#9B9890] tabular-nums truncate w-full text-center">
            {monthLabel(lab)}
          </span>
        </div>
      ))}
    </div>
  );
}

function TrendsSection({ trends, lens }) {
  const isProvider = lens === "provider";
  const months = trends.months || [];
  const sessTotal = trends.sessions?.total || [];
  const sessCompleted = trends.sessions?.completed || [];
  const sessCancelled = trends.sessions?.cancelled || [];
  const completionRates = trends.sessions?.completion_rate || [];
  // Iter 45h — Use `iep_size` so this trend line matches the live
  // "Students with IEPs" tile exactly. `trends.caseload.size` is the
  // broader enrolled series (includes RTI/Monitoring + graduated-but-
  // not-yet-archived) and would show 19 today vs the tile's 18.
  const caseloadSize = trends.caseload?.iep_size || trends.caseload?.size || [];
  const added = trends.caseload?.added || [];
  const exited = trends.caseload?.exited || [];
  const iepBurden = trends.iep_due_burden || [];
  const metBurden = trends.met_due_burden || [];
  const cancelByReason = trends.cancellations_by_reason || {};

  const lastSize = caseloadSize.length ? caseloadSize[caseloadSize.length - 1] : 0;
  const firstSize = caseloadSize.length ? caseloadSize[0] : 0;
  const sizeDelta = lastSize - firstSize;
  const totalAdded = added.reduce((a, b) => a + b, 0);
  const totalExited = exited.reduce((a, b) => a + b, 0);
  const totalCompleted = sessCompleted.reduce((a, b) => a + b, 0);
  const totalSessions = sessTotal.reduce((a, b) => a + b, 0);
  const periodCompletionRate = totalSessions > 0 ? totalCompleted / totalSessions : null;
  const totalCancelled = sessCancelled.reduce((a, b) => a + b, 0);
  const lastIep = iepBurden.length ? iepBurden[iepBurden.length - 1] : 0;
  const lastMet = metBurden.length ? metBurden[metBurden.length - 1] : 0;

  const reasonTotals = Object.entries(cancelByReason)
    .map(([reason, arr]) => [reason, (arr || []).reduce((a, b) => a + b, 0)])
    .sort((a, b) => b[1] - a[1]);
  const reasonMax = Math.max(1, ...reasonTotals.map(([, v]) => v));

  return (
    <Section
      title={isProvider ? "My Trends" : "Trends"}
      subtitle={`Last ${months.length} month${months.length === 1 ? "" : "s"} · ${months[0] || "—"} → ${months[months.length - 1] || "—"}`}
      icon={TrendUp}
      id="trends"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile
          testid="trend-caseload-now"
          label={isProvider ? "My caseload now" : "Caseload now"}
          value={lastSize}
          accent="slate"
          sub={
            sizeDelta === 0
              ? "no change in window"
              : sizeDelta > 0
                ? `+${sizeDelta} since ${monthLabel(months[0])}`
                : `${sizeDelta} since ${monthLabel(months[0])}`
          }
        />
        <MetricTile
          testid="trend-net-flow"
          label="Added · Exited"
          value={`${totalAdded} · ${totalExited}`}
          sub={`net ${totalAdded - totalExited >= 0 ? "+" : ""}${totalAdded - totalExited} in window`}
        />
        <MetricTile
          testid="trend-completion-rate"
          label="Completion rate"
          value={fmtPct(periodCompletionRate)}
          accent={periodCompletionRate != null && periodCompletionRate >= 0.85 ? "green" : "default"}
          sub={`${totalCompleted} / ${totalSessions} sessions`}
        />
        <MetricTile
          testid="trend-cancellations"
          label="Cancellations"
          value={totalCancelled}
          accent={totalCancelled > 0 ? "amber" : "default"}
          sub={reasonTotals[0] ? `top: ${CANCEL_REASON_LABELS[reasonTotals[0][0]] || reasonTotals[0][0]}` : "no cancellations"}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <ReportCard
          title={isProvider ? "My caseload over time" : "Caseload size over time"}
          subtitle="Students with IEPs at month end"
          id="trend-caseload-size"
        >
          {caseloadSize.every((v) => v === 0) ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No caseload history in window.</p>
          ) : (
            <>
              <Sparkline values={caseloadSize} tone="#1E5A3F" height={40} />
              <div className="flex justify-between mt-1.5 gap-1">
                {months.map((m, i) => (
                  <div key={m} className="text-center min-w-0 flex-1">
                    <p className="text-[8px] text-[#9B9890] tabular-nums truncate">{monthLabel(m)}</p>
                    <p className="text-[10px] font-medium text-[#1F2937] tabular-nums">{caseloadSize[i]}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </ReportCard>

        <ReportCard
          title={isProvider ? "My students added vs exited" : "Students added vs exited"}
          subtitle="Per month"
          id="trend-flow"
        >
          {totalAdded === 0 && totalExited === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No movement in window.</p>
          ) : (
            <>
              <PairedBars a={added} b={exited} labels={months} toneA="bg-[#1E5A3F]" toneB="bg-[#9C6B2E]" height={48} />
              <div className="flex items-center gap-3 mt-2 text-[10px] text-[#6B7280]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#1E5A3F] rounded-sm" />Added · {totalAdded}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#9C6B2E] rounded-sm" />Exited · {totalExited}</span>
              </div>
            </>
          )}
        </ReportCard>

        <ReportCard
          title={isProvider ? "My sessions completed" : "Sessions completed"}
          subtitle="Per month + completion rate"
          id="trend-sessions"
        >
          {totalSessions === 0 ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No sessions in window.</p>
          ) : (
            <>
              <MiniBars values={sessCompleted} labels={months} tone="bg-[#1E3F33]" height={48} />
              <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-[#ECEAE4]">
                <p className="text-[10px] text-[#6B7280]">Monthly completion</p>
                <div className="flex gap-1.5 flex-wrap justify-end">
                  {completionRates.map((r, i) => (
                    <span key={i} className="text-[10px] font-medium text-[#1F2937] tabular-nums" title={monthLabel(months[i])}>
                      {r == null ? "—" : `${Math.round(r * 100)}%`}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </ReportCard>

        <ReportCard
          title="Cancellations by reason"
          subtitle={totalCancelled === 0 ? "No cancellations in window" : "Window total · click to expand"}
          id="trend-cancellations"
        >
          {/* Iter 37 — Cancellations card now collapsible. Lower visual
              priority by default so it doesn't dominate the trends grid;
              users open it when they want a breakdown. */}
          {totalCancelled === 0 ? (
            <p className="text-[11px] text-[#1E5A3F] py-2">No cancellations in window.</p>
          ) : (
            <details className="group" data-testid="cancellations-details">
              <summary className="flex items-center justify-between cursor-pointer list-none py-1 hover:text-[#1F2937]">
                <span className="text-[11px] text-[#6B7280] tabular-nums">
                  {totalCancelled} cancelled · top: {CANCEL_REASON_LABELS[reasonTotals[0]?.[0]] || reasonTotals[0]?.[0] || "—"}
                </span>
                <span className="text-[11px] text-[#6B7280] group-open:hidden">+ Expand</span>
                <span className="text-[11px] text-[#6B7280] hidden group-open:inline">− Collapse</span>
              </summary>
              <div className="mt-2">
                <MiniBars values={sessCancelled} labels={months} tone="bg-[#9C6B2E]" height={36} />
                <div className="space-y-1.5 mt-3 pt-2 border-t border-[#ECEAE4]">
                  {reasonTotals.slice(0, 4).map(([reason, count]) => (
                    <HBar
                      key={reason}
                      label={CANCEL_REASON_LABELS[reason] || reason}
                      count={count}
                      max={reasonMax}
                      tone="bg-[#9C6B2E]"
                    />
                  ))}
                </div>
              </div>
            </details>
          )}
        </ReportCard>

        <ReportCard
          title="IEP & MET due burden"
          subtitle={`Compliance load · ${lastIep} IEPs · ${lastMet} METs this month`}
          id="trend-due-burden"
          className="md:col-span-2"
        >
          {iepBurden.every((v) => v === 0) && metBurden.every((v) => v === 0) ? (
            <p className="text-[11px] text-[#6B7280] italic py-2">No upcoming IEP or MET due dates in window.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] font-medium text-[#1F2937]">IEPs due</p>
                  <p className="text-[10px] text-[#6B7280] tabular-nums">total {iepBurden.reduce((a, b) => a + b, 0)}</p>
                </div>
                <MiniBars values={iepBurden} labels={months} tone="bg-[#3F4756]" height={36} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] font-medium text-[#1F2937]">METs due</p>
                  <p className="text-[10px] text-[#6B7280] tabular-nums">total {metBurden.reduce((a, b) => a + b, 0)}</p>
                </div>
                <MiniBars values={metBurden} labels={months} tone="bg-[#7C3F58]" height={36} />
              </div>
            </div>
          )}
        </ReportCard>

        {/* Iter 37 — Admin-only trend expansion */}
        {!isProvider && (
          <>
            <LongTermDeltas longTerm={trends.long_term} />
            <SpikeAlerts spikes={trends.spikes || []} months={months} />
            <CategoryTrendCard
              title="Eligibility mix over time"
              subtitle="Primary category · current snapshot per month"
              id="trend-eligibility"
              series={trends.eligibility_by_month || {}}
              months={months}
              tone="bg-[#7C3F58]"
              labelMap={ELIGIBILITY_LABELS}
              emptyHint="No students carry an eligibility category yet. Set one on a student to start tracking the mix."
            />
            <CategoryTrendCard
              title="Areas of Impact mix over time"
              subtitle="Broad Areas of Impact · students per area per month"
              id="trend-impact"
              series={trends.impact_by_month || {}}
              months={months}
              tone="bg-[#1E5A3F]"
              labelMap={IMPACT_LABELS}
              emptyHint="No Areas of Impact data — set Areas of Impact under a student's eligibility to populate this."
            />
          </>
        )}
      </div>

      <p className="text-[10px] text-[#9B9890] italic mt-2">
        {isProvider
          ? "Trends derive from real time-stamped records — your sessions, caseload, and due-date data."
          : "Trends derive from real time-stamped records. Eligibility / Areas of Impact mix uses each student's CURRENT selections against their active-window for that month — accumulation over time, not retroactive shifts."}
      </p>
    </Section>
  );
}


// ── Iter 37 — Admin trend expansion components ───────────────────────────
// CategoryTrendCard renders a per-category sparkline strip + a sortable
// bar list of latest-month counts. Used for both Eligibility Mix and
// Service Domain Mix on the admin Trends section.
function CategoryTrendCard({ title, subtitle, id, series, months, tone, labelMap, emptyHint }) {
  const entries = Object.entries(series || {});
  const totals = entries.map(([k, arr]) => [k, (arr || []).reduce((a, b) => a + b, 0), arr]);
  const sorted = totals
    .filter(([, total]) => total > 0)
    .sort((a, b) => b[2][b[2].length - 1] - a[2][a[2].length - 1]); // sort by latest month
  const max = Math.max(1, ...sorted.map(([, , arr]) => Math.max(...arr)));

  return (
    <ReportCard title={title} subtitle={subtitle} id={id} className="md:col-span-2">
      {sorted.length === 0 ? (
        <p className="text-[11px] text-[#6B7280] italic py-2">{emptyHint || "No data in window."}</p>
      ) : (
        <div className="space-y-2 mt-1">
          {sorted.slice(0, 8).map(([key, total, arr]) => {
            const latest = arr[arr.length - 1];
            const first = arr[0];
            const delta = latest - first;
            const trendClass = delta > 0 ? "text-[#1E5A3F]" : delta < 0 ? "text-[#9C6B2E]" : "text-[#6B7280]";
            const trendArrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "—";
            return (
              <div key={key} className="grid grid-cols-[140px_1fr_60px_50px] gap-2 items-center text-[11px]">
                <span className="text-[#1F2937] font-medium truncate" title={key}>
                  {labelMap?.[key] || labelMap?.[key.toLowerCase()] || key}
                </span>
                <div className="flex items-end gap-[2px] h-7">
                  {arr.map((v, i) => {
                    const pct = (v / max) * 100;
                    return (
                      <div
                        key={i}
                        className={`${tone} flex-1 rounded-sm`}
                        style={{ height: `${pct}%`, minHeight: v > 0 ? "2px" : "0", opacity: i === arr.length - 1 ? 1 : 0.6 }}
                        title={`${months[i]}: ${v}`}
                      />
                    );
                  })}
                </div>
                <span className="text-[#1F2937] font-semibold tabular-nums text-right">{latest}</span>
                <span className={`tabular-nums text-right ${trendClass}`}>
                  {trendArrow} {delta === 0 ? "" : Math.abs(delta)}
                </span>
              </div>
            );
          })}
          {sorted.length > 8 && (
            <p className="text-[10px] text-[#9B9890] mt-1">+{sorted.length - 8} more</p>
          )}
        </div>
      )}
    </ReportCard>
  );
}

// SpikeAlerts surfaces categories with significant MoM movement so the
// admin doesn't have to scan every row. Empty-state stays clean — we
// only render the card when we actually have something to flag.
function SpikeAlerts({ spikes, months }) {
  if (!spikes || spikes.length === 0) return null;
  const top = spikes.slice(0, 5);

  const labelFor = (s) =>
    s.kind === "eligibility"
      ? (ELIGIBILITY_LABELS[s.key] || ELIGIBILITY_LABELS[s.key?.toLowerCase()] || s.key)
      : (IMPACT_LABELS[s.key] || s.key);

  return (
    <ReportCard
      title="What's moving this month"
      subtitle={`Significant changes vs prior months · ${months[months.length - 1] || "—"}`}
      id="trend-spikes"
      className="md:col-span-2"
    >
      <div className="space-y-1.5 mt-1">
        {top.map((s) => {
          const up = s.direction === "up";
          return (
            <div
              key={`${s.kind}:${s.key}`}
              data-testid={`spike-${s.kind}-${s.key}`}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md text-[12px] ${
                up ? "bg-[#FFF7E8] border border-[#F2D9B5]" : "bg-[#F0EFE9] border border-[#E5E3DB]"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${up ? "text-[#9C6B2E]" : "text-[#3F4756]"}`}>
                  {s.kind === "eligibility" ? "Eligibility" : "Areas of Impact"}
                </span>
                <span className="text-[#1F2937] font-medium truncate">{labelFor(s)}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0 tabular-nums">
                <span className="text-[#6B7280]">prior avg {s.prior_avg}</span>
                <span className="text-[#1F2937] font-semibold">now {s.current}</span>
                <span className={`font-semibold ${up ? "text-[#9C6B2E]" : "text-[#1E5A3F]"}`}>
                  {up ? "↑" : "↓"} {Math.round(Math.abs(s.pct_change) * 100)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </ReportCard>
  );
}


// ── Iter 38 — Long-term multi-window deltas ──────────────────────────────
// Shows caseload + top eligibility / domain rows at fixed lookbacks
// (1mo / 3mo / 6mo / 1yr / 3yr / 5yr) with per-cell delta vs now.
// "—" cells are honest: data doesn't go back that far yet.
function LongTermDeltas({ longTerm }) {
  if (!longTerm) return null;
  const windows = longTerm.windows || [];
  const caseload = longTerm.caseload;

  const fmtDeltaCell = (now, win, delta) => {
    if (!win || !win.available) {
      return { display: "—", pct: null, dim: true, source: win?.source };
    }
    const count = win.count;
    if (count == null) return { display: "—", pct: null, dim: true, source: win.source };
    const abs = delta?.abs;
    const pct = delta?.pct;
    return {
      display: `${count}`,
      delta: abs,
      pct,
      dim: false,
      source: win.source,
    };
  };

  const fmtArrow = (pct, abs) => {
    if (pct == null || abs == null || abs === 0) return null;
    const up = abs > 0;
    const pctNum = Math.round(Math.abs(pct) * 100);
    return (
      <span className={`text-[9px] font-medium tabular-nums ml-1 ${up ? "text-[#1E5A3F]" : "text-[#9C6B2E]"}`}>
        {up ? "↑" : "↓"}{pctNum}%
      </span>
    );
  };

  const renderRow = (label, now, windowsObj, deltasObj, key) => (
    <tr key={key} className="border-t border-[#ECEAE4]" data-testid={`lt-row-${key}`}>
      <td className="py-2 pr-3 text-[12px] text-[#1F2937] font-medium align-top whitespace-nowrap">
        {label}
      </td>
      <td className="py-2 px-2 text-[12px] text-[#1F2937] font-semibold tabular-nums text-right">
        {now == null ? "—" : now}
      </td>
      {windows.map((w) => {
        const win = windowsObj?.[w.key];
        const delta = deltasObj?.[w.key];
        const cell = fmtDeltaCell(now, win, delta);
        const sourceColor =
          cell.source === "snapshot"
            ? "bg-[#1E5A3F]"
            : cell.source === "derived"
              ? "bg-[#9C6B2E]"
              : "";
        const tooltip = win?.actual_snapshot_date
          ? `Snapshot · ${win.actual_snapshot_date}`
          : win?.target_iso
            ? cell.source === "derived"
              ? `Derived from current state · target ${win.target_iso}`
              : `Target ${win.target_iso}`
            : undefined;
        return (
          <td
            key={w.key}
            className={`py-2 px-2 text-right tabular-nums ${cell.dim ? "text-[#9B9890]" : "text-[#4B5563]"}`}
            title={tooltip}
          >
            <span className="inline-flex items-center gap-1 justify-end">
              {sourceColor && !cell.dim && (
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${sourceColor}`} aria-hidden />
              )}
              <span className="text-[12px]">{cell.display}</span>
              {!cell.dim && fmtArrow(cell.pct, cell.delta)}
            </span>
          </td>
        );
      })}
    </tr>
  );

  return (
    <ReportCard
      title="Long-term trends"
      subtitle="Point-in-time snapshots vs now · windows where data isn't yet available render as —"
      id="trend-long-term-deltas"
      className="md:col-span-2"
    >
      <div className="overflow-x-auto -mx-1">
        <table className="w-full" data-testid="long-term-deltas-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[#9B9890]">
              <th className="py-1 pr-3 text-left font-semibold">Dimension</th>
              <th className="py-1 px-2 text-right font-semibold">Now</th>
              {windows.map((w) => (
                <th key={w.key} className="py-1 px-2 text-right font-semibold whitespace-nowrap">
                  {w.label} ago
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Caseload — total students with IEPs */}
            {caseload &&
              renderRow(
                "Caseload size",
                caseload.now,
                caseload.windows,
                caseload.deltas,
                "caseload"
              )}
            {(longTerm.eligibility || []).slice(0, 5).map((e) =>
              renderRow(
                ELIGIBILITY_LABELS[e.key] || ELIGIBILITY_LABELS[e.key?.toLowerCase()] || e.key,
                e.now,
                Object.fromEntries(
                  Object.entries(e.windows || {}).map(([k, v]) => [k, v])
                ),
                e.deltas,
                `elig-${e.key}`
              )
            )}
            {(longTerm.impact || []).slice(0, 5).map((e) =>
              renderRow(
                IMPACT_LABELS[e.key] || e.key,
                e.now,
                Object.fromEntries(
                  Object.entries(e.windows || {}).map(([k, v]) => [k, v])
                ),
                e.deltas,
                `impact-${e.key}`
              )
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-3 pt-2 border-t border-[#ECEAE4] text-[10px] text-[#6B7280]" data-testid="long-term-legend">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1E5A3F]" />
          Snapshot · true historical
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#9C6B2E]" />
          Derived · current-state filter
        </span>
        <span>— · no data yet</span>
      </div>
      <p className="text-[10px] text-[#9B9890] italic mt-2 leading-relaxed">
        Snapshot cells lock in what each student looked like on that date. Derived cells use the
        current eligibility/services against the active-window for that date — accumulation, not
        retroactive shifts. Future re-classifications won't rewrite snapshot history.
      </p>
    </ReportCard>
  );
}


// ── Iter 39 — Snapshot status bar (admin only) ────────────────────────────
// Small inline strip below the page title showing the snapshot system's
// health: earliest captured date · latest · whether today is locked in.
// Two actions: capture today on demand (idempotent) and backfill any
// missing days from the earliest student forward.
function SnapshotStatusBar() {
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState("");

  const refresh = async () => {
    try {
      const r = await api.getSnapshotCoverage();
      setCoverage(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { refresh(); }, []);

  const captureNow = async () => {
    setBusy("capture");
    try {
      const r = await api.captureSnapshotToday();
      if (r.data.skipped) toast.info("Today's snapshot is already locked in");
      else toast.success(`Captured ${r.data.row_count} student rows for today`);
      await refresh();
    } catch (e) {
      toast.error("Capture failed", { description: e.message });
    } finally {
      setBusy("");
    }
  };

  const backfill = async () => {
    setBusy("backfill");
    try {
      const r = await api.backfillSnapshots();
      const written = r.data.days_written ?? 0;
      const skipped = r.data.skipped ?? 0;
      toast.success(`Backfill complete · ${written} day${written === 1 ? "" : "s"} written, ${skipped} already covered`);
      await refresh();
    } catch (e) {
      toast.error("Backfill failed", { description: e.message });
    } finally {
      setBusy("");
    }
  };

  if (!coverage) {
    return (
      <p className="text-[10px] text-[#9B9890] mt-1.5" data-testid="snapshot-status-loading">
        Snapshot system: loading…
      </p>
    );
  }

  const { total_rows, distinct_dates, earliest_date, latest_date, today_captured } = coverage;
  const empty = total_rows === 0;

  return (
    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[10px]" data-testid="snapshot-status">
      <span className="inline-flex items-center gap-1.5 text-[#6B7280]">
        <Camera size={11} weight="duotone" className="text-[#1E5A3F]" />
        <span className="font-medium text-[#3F4756]">Snapshot system</span>
        {empty ? (
          <span className="text-[#9C6B2E]">no snapshots yet</span>
        ) : (
          <>
            <span className={today_captured ? "text-[#1E5A3F]" : "text-[#9C6B2E]"}>
              {today_captured ? "today locked in" : "today not captured"}
            </span>
            <span className="text-[#9B9890]">
              · {distinct_dates} day{distinct_dates === 1 ? "" : "s"} · {earliest_date} → {latest_date} · {total_rows.toLocaleString()} rows
            </span>
          </>
        )}
      </span>
      <div className="flex items-center gap-2">
        <button
          data-testid="snapshot-capture-now"
          onClick={captureNow}
          disabled={busy !== ""}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-[#1E3F33] hover:bg-[#E8F0EB] border border-[#E5E3DB] transition-colors disabled:opacity-50"
          title="Capture today's snapshot now (idempotent)"
        >
          <Camera size={11} weight={busy === "capture" ? "fill" : "duotone"} />
          {busy === "capture" ? "Capturing…" : "Capture today"}
        </button>
        <button
          data-testid="snapshot-backfill"
          onClick={backfill}
          disabled={busy !== ""}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-[#3F4756] hover:bg-[#EDF2F7] border border-[#E5E3DB] transition-colors disabled:opacity-50"
          title="Fill any missing daily snapshots back to the earliest student"
        >
          <ArrowsClockwise size={11} weight={busy === "backfill" ? "fill" : "duotone"} />
          {busy === "backfill" ? "Filling…" : "Backfill gaps"}
        </button>
      </div>
    </div>
  );
}
